import { AfterViewChecked, Component, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { HasAnalytics, HasPageTitleAndMetaData, StaticPageInterface } from '@app/interfaces/_index';
import { GoogleAnalyticsService, QuickStartGuideService } from '@app/services/_index';
import { logGoogleAnalytics, scrollIntoView } from '@app/utilities/_index';

import { StaticPageAdapter } from '@app/adapters/_index';
import { StaticPage } from '@app/models/_index';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-quick-start-quide',
  templateUrl: './quick-start-quide.component.html',
  styleUrls: ['./quick-start-quide.component.scss'],
  standalone: false,
})
export class QuickStartGuideComponent
  implements OnInit, OnDestroy, AfterViewChecked, HasPageTitleAndMetaData, HasAnalytics
{
  // Page Properties
  quickStartGuide: StaticPage;
  parentNavItems: StaticPage[] = []; // Parent nav items
  childNavItems: StaticPage[] = []; // Child nav items
  subs: Subscription[] = [];
  fragment: string;
  isLoading: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private quickStartGuideService: QuickStartGuideService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private staticPageAdapter: StaticPageAdapter,
    private titleService: Title,
    private metaService: Meta
  ) {}

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics('CMS', 'click', dataLabel);
    this.googleAnalyticsService.sendEvent('CMS', 'click', dataLabel);
  }

  /**
   * On Init, call getQuickStartGuide to load data
   */
  ngOnInit(): void {
    this.isLoading = true;
    // Set page title and metadata
    this.setTitleAndMetaData();
    // Load quick start guide or training support content from CMS
    this.subs.push(
      this.route.params.subscribe((params: Params) => {
        this.getQuickStartGuide(params);
      })
    );
  }

  /**
   * Get quick start guide response from wordpress service
   */
  getQuickStartGuide(params: Params): void {
    this.subs.push(
      this.quickStartGuideService.getQuickStartGuide().subscribe({
        next: (response: StaticPageInterface[]) => {
          this.isLoading = false;
          if (response && response.length > 0) {
            // Redirect to 404 page if the slug does not matches.
            if (!response.some((item: StaticPageInterface) => item.slug === params.slug)) {
              this.router.navigateByUrl('pagenotfound');
            }
            // TODO: This is repetitive in other pages, need to refactor
            const staticPages: StaticPage[] = response.map((item: StaticPageInterface) => {
              return this.staticPageAdapter.adapt(item);
            });

            // Filter out the parent quick start guide items that have no children if parent id is 0
            this.parentNavItems = staticPages.filter((staticPage) => staticPage.parent === 0);

            /* this.parentNavItems.filter((parentItem) => {
                parentItem.isExpanded = false;
              });*/

            this.childNavItems = staticPages.filter((staticPage) => staticPage.parent !== 0);
            /* istanbul ignore else */
            if (staticPages && staticPages.length > 0) {
              const filteredStaticPage = staticPages.filter(
                (staticPage) => staticPage.slug === params.slug
              );
              if (filteredStaticPage && filteredStaticPage.length > 0) {
                this.quickStartGuide = staticPages.filter(
                  (staticPage) => staticPage.slug === params.slug
                )[0];
              }
            }
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Getting Started content completed');
        },
      })
    );
    // The fragment allows the link # page jump to jump to specific content on page
    this.subs.push(
      this.route.fragment.subscribe((fragment: string) => {
        this.fragment = fragment;
      })
    );
  }

  /**
   * Set quick start guide title and metadata tags
   */
  setTitleAndMetaData(): void {
    // NOTE: Update page title on Quick Start Guide page
    this.titleService.setTitle('Getting Started Guide | Genesys Education');
    // NOTE: Update meta tag description for page
    this.metaService.updateTag({
      name: 'description',
      content:
        'See all of your Genesys Education enrollments, certifications, and recommended learning in one place.',
    });
  }

  /**
   * Scroll to View using fragment or link a href # alias
   */
  ngAfterViewChecked(): void {
    if (this.fragment) {
      scrollIntoView(this.fragment, 'start');
    }
  }

  /**
   * On Destroy, unsubscribe subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }
}
